import { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Button } from './ui/button'
import { 
  Home, 
  Gamepad2, 
  Ship, 
  Globe, 
  FlaskConical, 
  Users, 
  Trophy,
  Shield,
  Crown,
  Zap,
  Settings,
  LogOut,
  Menu,
  X,
  User,
  Building,
  Wrench,
  Target,
  Search,
  MessageCircle,
  Star,
  Factory,
  Eye,
  BookOpen,
  Award,
  BarChart3,
  MapPin,
  Sword
} from 'lucide-react'

interface User {
  id: string
  name: string
  email: string
  level: number
  faction?: string
}

interface NavigationProps {
  user: User | null
  onLogout: () => void
}

const Navigation = ({ user, onLogout }: NavigationProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()

  const handleLogout = () => {
    onLogout()
    navigate('/')
  }

  const mainNavItems = [
    { path: '/dashboard', label: 'Command Center', icon: Home },
    { path: '/galaxy-map', label: 'Galaxy Map', icon: Globe },
    { path: '/fleet-management', label: 'Fleet', icon: Ship },
    { path: '/combat', label: 'Combat', icon: Zap },
    { path: '/missions', label: 'Missions', icon: Target },
  ]

  const galaxyNavItems = [
    { path: '/colonies', label: 'Colonies', icon: Building },
    { path: '/exploration', label: 'Exploration', icon: Search },
    { path: '/construction', label: 'Construction', icon: Wrench },
    { path: '/shipyard', label: 'Shipyard', icon: Factory },
  ]

  const economyNavItems = [
    { path: '/market', label: 'Market', icon: Gamepad2 },
    { path: '/trading-post', label: 'Trading Post', icon: BarChart3 },
    { path: '/resource-management', label: 'Resources', icon: Star },
  ]

  const researchNavItems = [
    { path: '/research-center', label: 'Research Center', icon: FlaskConical },
    { path: '/laboratory', label: 'Laboratory', icon: BookOpen },
  ]

  const socialNavItems = [
    { path: '/community', label: 'Community', icon: Users },
    { path: '/alliances', label: 'Alliances', icon: Shield },
    { path: '/diplomacy', label: 'Diplomacy', icon: Crown },
    { path: '/messages', label: 'Messages', icon: MessageCircle },
    { path: '/rankings', label: 'Rankings', icon: Trophy },
  ]

  const profileNavItems = [
    { path: '/profile', label: 'Profile', icon: User },
    { path: '/achievements', label: 'Achievements', icon: Award },
    { path: '/statistics', label: 'Statistics', icon: BarChart3 },
    { path: '/intelligence', label: 'Intelligence', icon: Eye },
  ]

  return (
    <>
      {/* Desktop Navigation */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/20 backdrop-blur-md border-b border-white/10">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/dashboard" className="flex items-center space-x-2">
              <Crown className="w-8 h-8 text-purple-400" />
              <span className="text-xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                Crown of Suns
              </span>
            </Link>

            {/* Desktop Main Navigation */}
            <nav className="hidden lg:flex items-center space-x-1">
              {mainNavItems.map((item) => {
                const Icon = item.icon
                const isActive = location.pathname === item.path
                
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-purple-600/30 text-purple-300 border border-purple-500/30'
                        : 'text-gray-300 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{item.label}</span>
                  </Link>
                )
              })}
            </nav>

            {/* User Menu */}
            <div className="flex items-center space-x-4">
              {/* User Info */}
              <div className="hidden md:flex items-center space-x-3">
                <div className="text-right">
                  <div className="text-sm font-medium text-white">{user?.name}</div>
                  <div className="text-xs text-gray-400">Level {user?.level}</div>
                </div>
                <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                  <User className="w-4 h-4 text-white" />
                </div>
              </div>

              {/* Desktop Menu Buttons */}
              <div className="hidden md:flex items-center space-x-2">
                <Link to="/settings">
                  <Button variant="ghost" size="icon">
                    <Settings className="w-4 h-4" />
                  </Button>
                </Link>
                <Button variant="ghost" size="icon" onClick={handleLogout}>
                  <LogOut className="w-4 h-4" />
                </Button>
              </div>

              {/* Mobile Menu Button */}
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              >
                {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Navigation Menu */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="fixed inset-0 bg-black/50" onClick={() => setIsMobileMenuOpen(false)} />
          <div className="fixed top-16 left-0 right-0 bg-slate-900/95 backdrop-blur-md border-b border-white/10 max-h-[calc(100vh-4rem)] overflow-y-auto">
            <div className="container mx-auto px-4 py-6">
              {/* User Info Mobile */}
              <div className="flex items-center space-x-3 mb-6 pb-6 border-b border-white/10">
                <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="text-base font-medium text-white">{user?.name}</div>
                  <div className="text-sm text-gray-400">Level {user?.level}</div>
                </div>
              </div>

{/* Render navigation sections */}
              {[
                { title: 'Command Center', items: mainNavItems },
                { title: 'Galaxy', items: galaxyNavItems },
                { title: 'Economy', items: economyNavItems },
                { title: 'Research', items: researchNavItems },
                { title: 'Social', items: socialNavItems },
                { title: 'Profile', items: profileNavItems }
              ].map((section) => (
                <div key={section.title} className="space-y-2 mb-6">
                  <h3 className="text-xs uppercase tracking-wider text-gray-400 font-semibold mb-3">
                    {section.title}
                  </h3>
                  {section.items.map((item) => {
                    const Icon = item.icon
                    const isActive = location.pathname === item.path
                    
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={`flex items-center space-x-3 px-3 py-3 rounded-lg text-base font-medium transition-colors ${
                          isActive
                            ? 'bg-purple-600/30 text-purple-300 border border-purple-500/30'
                            : 'text-gray-300 hover:text-white hover:bg-white/10'
                        }`}
                      >
                        <Icon className="w-5 h-5" />
                        <span>{item.label}</span>
                      </Link>
                    )
                  })}
                </div>
              ))}

              {/* Mobile Actions */}
              <div className="space-y-2 pt-6 border-t border-white/10">
                <Link
                  to="/settings"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center space-x-3 px-3 py-3 rounded-lg text-base font-medium text-gray-300 hover:text-white hover:bg-white/10 transition-colors"
                >
                  <Settings className="w-5 h-5" />
                  <span>Settings</span>
                </Link>
                <button
                  onClick={() => {
                    setIsMobileMenuOpen(false)
                    handleLogout()
                  }}
                  className="flex items-center space-x-3 px-3 py-3 rounded-lg text-base font-medium text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors w-full text-left"
                >
                  <LogOut className="w-5 h-5" />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default Navigation